import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

type Plan = "free" | "pro" | "premium";

interface PlanConfig {
  name: string;
  price: string;
  planKey: Plan;
  features: string[];
}

const plans: PlanConfig[] = [
  {
    name: "Free",
    price: "$0",
    planKey: "free",
    features: [
      "1,200 words per submission",
      "3 submissions per day",
      "Basic rewriting access",
    ],
  },
  {
    name: "Pro",
    price: "$5.99 / month",
    planKey: "pro",
    features: [
      "3,000 words per submission",
      "50 submissions per day",
      "Faster processing",
      "Priority rewrite quality",
    ],
  },
  {
    name: "Premium",
    price: "$10.99 / month",
    planKey: "premium",
    features: [
      "12,000 words per submission",
      "Unlimited submissions",
      "Maximum rewrite depth",
      "Highest priority processing",
    ],
  },
];

export default function Pricing() {
  const { user, isLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();

 const handleUpgrade = async (plan: Plan) => {
  if (!isLoggedIn) {
    navigate("/login");
    return;
  }

  try {
    const res = await api.post(
      "/api/billing/checkout",
      { plan },
      { withCredentials: true }
    );

    window.location.href = res.data.url;
  } catch (err) {
    console.error("Checkout failed", err);
  }
};
 

  return (
    <div className="page-shell px-4 py-14 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 text-center">
          <h1 className="mb-4 text-4xl font-extrabold text-secondary">
            Generate more. Remove limits.
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-textMuted">
            DeCipher scales with how much you write — start free, upgrade only
            when you hit the ceiling.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {plans.map((plan) => {
            const isCurrentPlan = user?.plan === plan.planKey;
            const isFree = plan.planKey === "free";
            const isFeatured = plan.planKey === "pro";

            return (
              <div
                key={plan.planKey}
                className={`ui-card relative flex flex-col p-6 ${
                  isFeatured ? "border-primary shadow-xl shadow-primary/10" : ""
                }`}
              >
                {isFeatured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    Most Popular
                  </span>
                )}

                <h2 className="mb-2 text-2xl font-semibold text-secondary">
                  {plan.name}
                </h2>

                <p className="mb-6 text-3xl font-bold text-secondary">
                  {plan.price}
                </p>

                <ul className="mb-6 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-textMuted">
                      <span className="mt-0.5 text-success">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {!isFree && (
                  <button
                    disabled={isCurrentPlan}
                    onClick={() => handleUpgrade(plan.planKey)}
                    className={`w-full py-3.5 rounded-xl font-semibold transition
                      ${
                        isCurrentPlan
                          ? "cursor-not-allowed bg-slate-300 text-slate-600"
                          : "btn-primary"
                      }
                    `}
                  >
                    {isCurrentPlan ? "Current Plan" : `Upgrade to ${plan.name}`}
                  </button>
                )}
              </div>


            );
          })}
        </div>

        <div className="mx-auto mt-14 max-w-3xl rounded-2xl border border-slate-200 bg-white/80 px-5 py-4 text-center text-sm text-textMuted">
          <p className="leading-relaxed">
            <strong className="text-secondary">All charges are non-refundable.</strong> Subscriptions automatically renew monthly until canceled.
            You may cancel anytime and retain access until the end of your billing period.
            No partial refunds for mid-month cancellations.
          </p>
        </div>
      </div>
    </div>
  );
}
